import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Undo2, Lightbulb, RotateCcw, Coins, Trophy, Lock } from "lucide-react";
import ArcadeNav from "../../arcade/ArcadeNav";
import { useAuth } from "../../contexts/AuthContext";
import { usePresence } from "../../game/presence";
import Card from "./Card";
import { SUIT_META } from "./cardThemes";
import useSEO from "../../seo/useSEO";
import { SEO_GAMES } from "../../seo/seoData";
import GameSEOContent from "../../seo/GameSEOContent";
import ArcadeFooter from "../../arcade/ArcadeFooter";
import {
  newGame, drawFromStock, tryMove, autoMove, undo, findHint, isWin,
  validTableauStack, canPlaceOnFoundation,
} from "./SolitaireEngine";
import "./solitaire.css";
import "../../arcade/arcade.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Non-VIP undo budget per game; VIP unlimited; Founder unlimited.
const NON_VIP_UNDOS = 3;

export default function SolitaireScreen() {
  useSEO(SEO_GAMES["zombie-solitaire"]);
  const { user, refresh } = useAuth();
  const isVip = !!(user && user.is_vip);
  const isFounder = !!(user && user.founder);

  const [drawMode, setDrawMode] = useState(null); // null = picker shown
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null); // { from }
  const [hint, setHint] = useState(null);         // { from, to }
  const [shake, setShake] = useState(null);       // { col?, idx?, foundationIdx?, waste? }
  const [drag, setDrag] = useState(null);         // pointer-drag in flight
  const [undosLeft, setUndosLeft] = useState(NON_VIP_UNDOS);
  const [hintsLeft, setHintsLeft] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [won, setWon] = useState(false);
  const [winReward, setWinReward] = useState(null);
  const tickRef = useRef(null);
  const shakeTimerRef = useRef(null);
  const boardRef = useRef(null);
  // Ping presence as "playing zombie-solitaire" while a game board is mounted
  // and not yet won. Powers the Players Online badge on the arcade homepage.
  usePresence(!!state && !won, "zombie-solitaire");

  // Reset undos/hints when a new game starts
  useEffect(() => {
    if (!state) return;
    setUndosLeft(isVip || isFounder ? Infinity : NON_VIP_UNDOS);
    setHintsLeft(isFounder ? 3 : (isVip ? 1 : 0));
  }, [state?.startTime, isVip, isFounder]);

  // Timer
  useEffect(() => {
    if (!state || won) return;
    tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [state?.startTime, won]);

  // Win detection + reward
  useEffect(() => {
    if (!state || won) return;
    if (isWin(state)) {
      setWon(true);
      clearInterval(tickRef.current);
      submitWin(state.moves, seconds, state.drawMode);
    }
  }, [state, won, seconds]);

  // ---- Auto-finish ---------------------------------------------------------
  // Once every tableau card is face-up, the game is mathematically won —
  // it's just busy-work routing everything to the foundations. So we
  // automate it: every 110ms find the next foundation move (waste top
  // first, then tableau exposed cards). Draws/recycles stock as needed.
  // Disables user input while running.
  const [autoFinishing, setAutoFinishing] = useState(false);

  useEffect(() => {
    if (!state || won || autoFinishing) return;
    // Need at least one card still in play
    const totalInPlay = state.stock.length + state.waste.length + state.tableau.reduce((n, c) => n + c.length, 0);
    if (totalInPlay === 0) return;
    // All tableau cards must be face-up
    const allFaceUp = state.tableau.every((col) => col.every((c) => c.faceUp));
    if (!allFaceUp) return;
    setAutoFinishing(true);
    toast.success("Auto-completing!", { duration: 1500 });
  }, [state, won, autoFinishing]);

  useEffect(() => {
    if (!autoFinishing || !state || won) return;
    const t = setTimeout(() => {
      setState((s) => {
        if (!s) return s;
        // 1) Try waste top
        if (s.waste.length > 0) {
          const top = s.waste[s.waste.length - 1];
          for (let i = 0; i < 4; i++) {
            if (canPlaceOnFoundation(top, s.foundations[i])) {
              return tryMove(s, { type: "waste" }, { type: "foundation", idx: i }) || s;
            }
          }
        }
        // 2) Try each tableau column's bottom-exposed card
        for (let c = 0; c < 7; c++) {
          const col = s.tableau[c];
          if (col.length === 0) continue;
          const top = col[col.length - 1];
          for (let i = 0; i < 4; i++) {
            if (canPlaceOnFoundation(top, s.foundations[i])) {
              return tryMove(s, { type: "tableau", col: c, index: col.length - 1 }, { type: "foundation", idx: i }) || s;
            }
          }
        }
        // 3) Need to dig deeper — draw from stock
        if (s.stock.length > 0 || s.waste.length > 0) {
          return drawFromStock(s);
        }
        // No progress possible — stop auto-finish.
        setAutoFinishing(false);
        return s;
      });
    }, 110);
    return () => clearTimeout(t);
  }, [autoFinishing, state, won]);

  async function submitWin(moves, secs, mode) {
    if (!user) {
      setWinReward({ coins_awarded: 0, anon: true });
      return;
    }
    try {
      const res = await axios.post(`${API}/games/solitaire/win`, {
        moves, seconds: secs, draw_mode: mode,
      });
      setWinReward(res.data);
      await refresh();
      toast.success(`+${res.data.coins_awarded} coins!`, { duration: 4000 });
    } catch (e) {
      setWinReward({ coins_awarded: 0, error: e?.response?.data?.detail || "Could not award coins" });
    }
  }

  function start(mode) {
    setDrawMode(mode);
    setState(newGame(mode));
    setSelected(null);
    setHint(null);
    setSeconds(0);
    setWon(false);
    setWinReward(null);
    setAutoFinishing(false);
  }

  function handleStockClick() {
    if (!state || won || autoFinishing) return;
    const next = drawFromStock(state);
    if (next) setState(next);
    setSelected(null);
    setHint(null);
  }

  function flashShake(target) {
    setShake(target);
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    shakeTimerRef.current = setTimeout(() => setShake(null), 450);
  }

  // ---- Pointer drag (mobile + desktop unified) ------------------------------
  // Drag starts on pointerdown on a tableau or waste card. If the pointer
  // moves more than DRAG_THRESHOLD px we treat it as a drag (visual ghost
  // follows finger/cursor, hit-test on release). If it doesn't move, we
  // treat it as a tap and run the existing auto-move logic.
  const DRAG_THRESHOLD = 8;

  function startPointerDrag(e, from) {
    if (!state || won || autoFinishing) return;
    // Validate pickup BEFORE entering drag mode so we can give immediate
    // shake/toast feedback for buried cards.
    if (from.type === "tableau") {
      const col = state.tableau[from.col];
      const idx = from.index == null ? col.length - 1 : from.index;
      if (!col[idx] || !col[idx].faceUp) return;
      const slice = col.slice(idx);
      if (!validTableauStack(slice)) {
        flashShake({ tableau: from.col, index: idx });
        toast.error("Cards on top aren't in order — move them first", { duration: 2200 });
        return;
      }
    }
    // Snapshot the cards we'd be moving so the ghost can render them.
    let cards = [];
    if (from.type === "tableau") {
      const col = state.tableau[from.col];
      const idx = from.index == null ? col.length - 1 : from.index;
      cards = col.slice(idx);
    } else if (from.type === "waste") {
      if (!state.waste.length) return;
      cards = [state.waste[state.waste.length - 1]];
    } else if (from.type === "foundation") {
      const f = state.foundations[from.idx];
      if (!f.length) return;
      cards = [f[f.length - 1]];
    }
    if (!cards.length) return;

    // Anchor the ghost at the same offset within the card where the pointer
    // landed, so the card appears to "stick" to the finger.
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    setDrag({
      from,
      cards,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      pointerId: e.pointerId,
      isDragging: false,
    });
    setHint(null);
  }

  // Find the drop target (tableau col or foundation idx) under the pointer.
  function dropTargetFromPoint(x, y) {
    const el = document.elementFromPoint(x, y);
    let cur = el;
    while (cur && cur !== document.body) {
      if (cur.dataset && cur.dataset.dropTab != null) {
        return { type: "tableau", col: parseInt(cur.dataset.dropTab, 10) };
      }
      if (cur.dataset && cur.dataset.dropFoundation != null) {
        return { type: "foundation", idx: parseInt(cur.dataset.dropFoundation, 10) };
      }
      cur = cur.parentElement;
    }
    return null;
  }

  // Window-level move/up listeners while dragging.
  useEffect(() => {
    if (!drag) return;
    function onMove(ev) {
      const dx = ev.clientX - drag.startX;
      const dy = ev.clientY - drag.startY;
      const becameDrag = drag.isDragging || (Math.hypot(dx, dy) > DRAG_THRESHOLD);
      setDrag((d) => d ? { ...d, x: ev.clientX, y: ev.clientY, isDragging: becameDrag } : null);
    }
    function onUp(ev) {
      const d = drag;
      setDrag(null);
      if (!d) return;
      if (!d.isDragging) {
        // Treat as a tap — fall back to existing auto-move / select flow.
        pickUp(d.from);
        return;
      }
      // Real drag — hit test for drop target.
      const to = dropTargetFromPoint(ev.clientX, ev.clientY);
      // Forbid drop on same source column (silent snap-back).
      const droppedOnSelf = to && to.type === "tableau" && d.from.type === "tableau" && to.col === d.from.col;
      if (!to || droppedOnSelf) {
        // FORGIVING FALLBACK: if the user dragged but didn't land on a
        // valid drop zone (e.g. slight finger wobble on a tap, or dropped
        // near but not on the foundation), still try to route the card
        // logically via autoMove. This is the "I clearly meant to send
        // this King to its foundation" rescue path.
        const auto = autoMove(state, d.from);
        if (auto) { setState(auto); setSelected(null); }
        return;
      }
      const next = tryMove(state, d.from, to);
      if (next) {
        setState(next);
        setSelected(null);
        return;
      }
      // Drop on a SPECIFIC target failed — try autoMove rescue before
      // showing the "can't go there" toast.
      const auto = autoMove(state, d.from);
      if (auto) { setState(auto); setSelected(null); return; }
      flashShake(to.type === "tableau" ? { tableau: to.col } : { foundation: to.idx });
      toast.error("That card can't go there", { duration: 1600 });
    }
    function onCancel() { setDrag(null); }
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [drag, state]);

  function pickUp(from) {
    if (!state || won || autoFinishing) return;
    setHint(null);
    // If selecting the same source, just unselect.
    if (selected && JSON.stringify(selected.from) === JSON.stringify(from)) {
      setSelected(null);
      return;
    }
    // Tableau picks: validate the sub-stack BEFORE accepting selection so we
    // can tell the user why a "buried" card can't be lifted (cards on top
    // don't form a valid alternating-color run).
    if (from.type === "tableau") {
      const col = state.tableau[from.col];
      const idx = from.index == null ? col.length - 1 : from.index;
      if (col[idx] && !col[idx].faceUp) return;
      const slice = col.slice(idx);
      if (!validTableauStack(slice)) {
        flashShake({ tableau: from.col, index: idx });
        toast.error("Cards on top aren't in order — move them first", { duration: 2200 });
        setSelected(null);
        return;
      }
    }
    // Try auto-move on a fresh tap to a source with no prior selection.
    if (!selected) {
      const auto = autoMove(state, from);
      if (auto) { setState(auto); return; }
      setSelected({ from });
      return;
    }
    // We had a selection — treat this as a destination tap.
    const to = sourceToDest(from, state);
    if (to) {
      const next = tryMove(state, selected.from, to);
      if (next) { setState(next); setSelected(null); return; }
      // Invalid drop on this destination — shake it.
      flashShake(to.type === "tableau" ? { tableau: to.col } : { foundation: to.idx });
      toast.error("That card can't go there", { duration: 1800 });
    }
    // Else swap selection to the new source.
    setSelected({ from });
  }

  function handleDrop(to) {
    if (!state || !selected || won || autoFinishing) return;
    const next = tryMove(state, selected.from, to);
    if (next) { setState(next); setSelected(null); return; }
    flashShake(to.type === "tableau" ? { tableau: to.col } : { foundation: to.idx });
    toast.error("That card can't go there", { duration: 1800 });
  }

  function handleUndo() {
    if (!state || won) return;
    if (!(isVip || isFounder) && undosLeft <= 0) {
      toast.error("Out of undos — VIP gets unlimited undo");
      return;
    }
    const next = undo(state);
    if (!next) return;
    setState(next);
    setSelected(null);
    setHint(null);
    if (!(isVip || isFounder)) setUndosLeft((u) => u - 1);
  }

  function handleHint() {
    if (!state || won) return;
    if (hintsLeft <= 0) {
      toast.error(isVip || isFounder
        ? "No more hints this game"
        : "Hints are a VIP perk — upgrade to use them");
      return;
    }
    const h = findHint(state);
    if (!h) { toast.message("No moves found — try drawing"); return; }
    setHint(h);
    setHintsLeft((n) => n - 1);
    setTimeout(() => setHint(null), 2500);
  }

  // ----- Drawing the picker -----
  if (!state) {
    return (
      <div className="arcade-page solitaire-page" data-testid="solitaire-picker">
        <Toaster position="top-center" theme="dark" />
        <ArcadeNav />
        <main className="arcade-main">
          <Link to="/" className="coming-back" data-testid="solitaire-back-to-arcade">
            <ArrowLeft size={16} /> Back to Arcade
          </Link>
          <div className="zs-picker">
            <h1 className="zs-picker-title">Zombie Solitaire</h1>
            <p className="zs-picker-sub">Choose how you&apos;d like to play</p>
            <div className="zs-picker-modes">
              <button type="button" className="zs-mode-btn easy" onClick={() => start(1)} data-testid="solitaire-mode-1">
                <span className="zs-mode-badge">EASY</span>
                <span className="zs-mode-name">Draw 1</span>
                <span className="zs-mode-desc">Flip one card at a time. Higher win rate.</span>
              </button>
              <button type="button" className="zs-mode-btn hard" onClick={() => start(3)} data-testid="solitaire-mode-3">
                <span className="zs-mode-badge">CLASSIC</span>
                <span className="zs-mode-name">Draw 3</span>
                <span className="zs-mode-desc">Flip three cards. Real Klondike challenge. +25 bonus coins.</span>
              </button>
            </div>
            {user && (
              <div className="zs-picker-perks">
                {isFounder && <span><Trophy size={12} /> Founder: unlimited undo + 3 hints</span>}
                {!isFounder && isVip && <span><Trophy size={12} /> VIP: unlimited undo + 1 hint</span>}
                {!isVip && !isFounder && <span><Lock size={12} /> Free: 3 undos, no hints — upgrade for VIP perks</span>}
              </div>
            )}
          </div>
          <GameSEOContent gameId="zombie-solitaire" />
          <ArcadeFooter />
        </main>
      </div>
    );
  }

  // ----- Drawing the board -----
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return (
    <div className="arcade-page solitaire-page" data-testid="solitaire-board">
      <Toaster position="top-center" theme="dark" />
      <ArcadeNav />

      <main className="arcade-main solitaire-main">
        <div className="zs-toolbar" data-testid="solitaire-toolbar">
          <Link to="/" className="coming-back" data-testid="solitaire-back-to-arcade">
            <ArrowLeft size={14} /> Arcade
          </Link>
          <div className="zs-stats">
            <span><b>{mm}:{ss}</b></span>
            <span>· {state.moves} moves</span>
            <span>· draw {state.drawMode}</span>
          </div>
          <div className="zs-actions">
            <button type="button" className="zs-btn" onClick={handleUndo} data-testid="solitaire-undo">
              <Undo2 size={14} /> Undo
              {!(isVip || isFounder) && <span className="zs-btn-badge">{undosLeft}</span>}
            </button>
            <button type="button" className="zs-btn" onClick={handleHint} data-testid="solitaire-hint">
              <Lightbulb size={14} /> Hint
              <span className="zs-btn-badge">{hintsLeft}</span>
            </button>
            <button type="button" className="zs-btn" onClick={() => { setState(null); }} data-testid="solitaire-new-game">
              <RotateCcw size={14} /> New
            </button>
          </div>
        </div>

        {/* Top row: stock + waste + foundations */}
        <div className="zs-top-row">
          <div className="zs-stock-waste">
            <div className="zs-pile zs-stock" onClick={handleStockClick} data-testid="solitaire-stock">
              {state.stock.length > 0 ? (
                <Card card={{ id: "stock-top", suit: "S", rank: 1, faceUp: false }} draggable={false} />
              ) : (
                <div className="zs-pile-empty zs-pile-recycle"><RotateCcw size={20} /></div>
              )}
              <span className="zs-pile-count">{state.stock.length}</span>
            </div>
            <div className="zs-pile zs-waste" data-testid="solitaire-waste">
              {state.waste.slice(-3).map((c, i) => {
                const isTop = i === Math.min(2, state.waste.length - 1);
                const inDragStack = drag && drag.isDragging && drag.from.type === "waste" && isTop;
                return (
                  <div key={c.id} className={`zs-waste-card ${inDragStack ? "dragging-source" : ""}`} style={{ left: i * 18 }}>
                    <Card
                      card={c}
                      selected={isSelectedFrom(selected, { type: "waste" }) && isTop}
                      hinted={isHintedFrom(hint, { type: "waste" }) && isTop}
                      onPointerDown={isTop ? (e) => startPointerDrag(e, { type: "waste" }) : undefined}
                    />
                  </div>
                );
              })}
              {state.waste.length === 0 && <div className="zs-pile-empty" />}
            </div>
          </div>
          <div className="zs-foundations">
            {state.foundations.map((f, i) => {
              const top = f[f.length - 1];
              return (
                <div
                  key={i}
                  className={`zs-pile zs-foundation ${shake && shake.foundation === i ? "shake" : ""}`}
                  data-drop-foundation={i}
                  onClick={() => { if (selected) handleDrop({ type: "foundation", idx: i }); else if (top) pickUp({ type: "foundation", idx: i }); }}
                  data-testid={`solitaire-foundation-${i}`}
                >
                  {top ? (
                    <Card card={top} selected={isSelectedFrom(selected, { type: "foundation", idx: i })} />
                  ) : (
                    <div className="zs-pile-empty zs-foundation-empty">{SUIT_META[["H","D","S","C"][i]].symbol}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tableau */}
        <div className="zs-tableau" data-testid="solitaire-tableau" ref={boardRef}>
          {state.tableau.map((col, c) => (
            <div
              key={c}
              className={`zs-tab-col ${col.length === 0 ? "empty" : ""} ${shake && shake.tableau === c && shake.index == null ? "shake" : ""}`}
              data-drop-tab={c}
              onClick={() => { if (col.length === 0 && selected) handleDrop({ type: "tableau", col: c }); }}
              data-testid={`solitaire-tab-col-${c}`}
              // Each subsequent card offsets vertically by --tab-spread (24/18/14
              // depending on viewport). The column itself uses absolute-positioned
              // children, so we must explicitly grow its box to enclose them —
              // otherwise the cards spill below into the SEO section underneath
              // on mobile, which the user reported as "overlapping" content.
              style={{ "--tab-cards": col.length || 1 }}
            >
              {col.length === 0 && <div className="zs-pile-empty zs-tab-empty" />}
              {col.map((card, i) => {
                const inDragStack = drag && drag.isDragging && drag.from.type === "tableau" && drag.from.col === c && (drag.from.index ?? col.length - 1) <= i;
                return (
                  <div
                    key={card.id}
                    className={`zs-tab-card-wrap ${shake && shake.tableau === c && shake.index === i ? "shake" : ""} ${inDragStack ? "dragging-source" : ""}`}
                    style={{ top: `calc(${i} * var(--tab-spread))` }}
                    onPointerDown={card.faceUp ? (e) => startPointerDrag(e, { type: "tableau", col: c, index: i }) : undefined}
                  >
                    <Card
                      card={card}
                      selected={isSelectedFromTab(selected, c, i)}
                      hinted={isHintedFromTab(hint, c, i)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Drag ghost — renders the picked-up sub-stack at the pointer
            position via portal so it floats above the whole board. */}
        {drag && drag.isDragging && createPortal(
          <div
            className="zs-drag-ghost"
            style={{
              left: drag.x - drag.offsetX,
              top: drag.y - drag.offsetY,
            }}
            aria-hidden="true"
          >
            {drag.cards.map((c, i) => (
              <div key={c.id} className="zs-drag-ghost-card" style={{ top: i * 24 }}>
                <Card card={c} draggable={false} />
              </div>
            ))}
          </div>,
          document.body
        )}


        {won && (
          <div className="zs-win-overlay" data-testid="solitaire-win">
            <div className="zs-win-card">
              <div className="zs-win-icon"><Trophy size={48} /></div>
              <h2>You Won!</h2>
              <p className="zs-win-stats">{mm}:{ss} · {state.moves} moves · draw {state.drawMode}</p>
              {winReward && winReward.coins_awarded > 0 && (
                <div className="zs-win-reward" data-testid="solitaire-win-coins">
                  <Coins size={20} /> +{winReward.coins_awarded} coins
                  <div className="zs-win-reward-breakdown">
                    Base {winReward.base}
                    {winReward.speed_bonus > 0 && ` · Speed +${winReward.speed_bonus}`}
                    {winReward.mode_bonus > 0 && ` · Draw 3 +${winReward.mode_bonus}`}
                  </div>
                </div>
              )}
              {winReward && winReward.anon && (
                <p className="zs-win-anon">Sign in to earn coins for your wallet!</p>
              )}
              <div className="zs-win-buttons">
                <button type="button" className="zc-btn primary" onClick={() => setState(null)} data-testid="solitaire-win-play-again">
                  <RotateCcw size={16} /> Play again
                </button>
                <Link to="/" className="zc-btn ghost" data-testid="solitaire-win-arcade">
                  <ArrowLeft size={16} /> Back to Arcade
                </Link>
              </div>
            </div>
          </div>
        )}
        <GameSEOContent gameId="zombie-solitaire" />
        <ArcadeFooter />
      </main>
    </div>
  );
}

// ---------- Helpers (tap selection / hint highlight) ----------
function isSelectedFrom(sel, from) {
  if (!sel) return false;
  const a = sel.from, b = from;
  return a.type === b.type && (a.col ?? null) === (b.col ?? null) && (a.idx ?? null) === (b.idx ?? null);
}
function isSelectedFromTab(sel, col, idx) {
  if (!sel) return false;
  // Highlight EVERY card in the picked-up sub-stack so the user can see
  // exactly what they're moving. The "from" stores the topmost picked
  // card's index; everything below (higher idx in array) also moves.
  return sel.from.type === "tableau"
    && sel.from.col === col
    && (sel.from.index ?? -1) <= idx;
}
function isHintedFrom(h, from) {
  if (!h) return false;
  const a = h.from;
  return a.type === from.type && (a.col ?? null) === (from.col ?? null);
}
function isHintedFromTab(h, col, idx) {
  if (!h) return false;
  const a = h.from;
  if (a.type !== "tableau" || a.col !== col) return false;
  return (a.index ?? -1) === idx;
}
function sourceToDest(from, state) {
  // Used when player has selection then taps another source — treat the
  // second tap as if it were a destination on the corresponding pile.
  if (from.type === "tableau") return { type: "tableau", col: from.col };
  if (from.type === "foundation") return { type: "foundation", idx: from.idx };
  return null;
}
